import { Suspense, lazy, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import {
  BrowserRouter,
  MemoryRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { RecoilRoot, useRecoilState } from 'recoil';
import { ReactComponent as Spinner } from './assets/Spinner.svg';
import Loading from './components/Loading';
import Modal from './components/modal';
import useAuth from './hooks/useAuth';
import useAxios from './hooks/useAxios';
import { useFetchUser } from './hooks/useFetchUser';
import useIsElectron from './hooks/useIsElectron';
import Charge from './screens/Charge';
import GoogleCallbackElectron from './screens/auth/google/CallbackElectron';
import KakaoCallbackElectron from './screens/auth/kakao/CallbackElectron';
import { userState } from './states/userState';
import { alertMessage, getOrigin } from './utils';

import Banner from './components/Banner';
import MainAnnouncement from './components/MainAnnouncement';
import useAnnouncement from './hooks/useAnnouncement';
import useTutorial from './hooks/useTutorial';

const Announcement = lazy(() => import('./screens/Announcement'));
const AnnouncementDetail = lazy(() => import('./screens/AnnouncementDetail'));
const AnnouncementAdmin = lazy(() => import('./screens/AnnouncementAdmin'));
const Report = lazy(() => import('./screens/Report'));
const Signin = lazy(() => import('./screens/Signin'));
const Signup = lazy(() => import('./screens/Signup'));
const Upload = lazy(() => import('./screens/upload/index'));
const Detail = lazy(() => import('./screens/Detail'));
const Chatting = lazy(() => import('./screens/Chatting'));
const KakaoCallback = lazy(() => import('./screens/auth/kakao/Callback'));
const GoogleCallback = lazy(() => import('./screens/auth/google/Callback'));
const FindId = lazy(() => import('./screens/find/id'));
const FindPassword = lazy(() => import('./screens/find/password'));
const FindList = lazy(() => import('./screens/find/result'));
const ResetPassword = lazy(() => import('./screens/find/reset'));
const MyPage = lazy(() => import('./screens/MyPage'));
const Exit = lazy(() => import('./screens/Exit'));
const Home = lazy(() => import('./screens/Home'));
const Redirect = lazy(() => import('./screens/Redirect'));
const Download = lazy(() => import('./screens/Download'));
const Help = lazy(() => import('./screens/Help'));
const PaymentResult = lazy(() => import('./screens/payment/result'));
const PaymentFail = lazy(() => import('./screens/payment/fail'));
const PaymentCancel = lazy(() => import('./screens/payment/cancel'));
const PaymentElectronResult = lazy(() =>
  import('./screens/payment/electron/result')
);
const PaymentElectronFail = lazy(() =>
  import('./screens/payment/electron/fail')
);
const PaymentElectronCancel = lazy(() =>
  import('./screens/payment/electron/cancel')
);
const Privacy = lazy(() => import('./screens/policy/Privacy'));
const Terms = lazy(() => import('./screens/policy/Terms'));
const Period = lazy(() => import('./screens/policy/Period'));
const Refund = lazy(() => import('./screens/policy/Refund'));
const PersonalInfo = lazy(() => import('./screens/policy/PersonalInfo'));
const ThirdInfo = lazy(() => import('./screens/policy/ThirdInfo'));
const PayGateway = lazy(() => import('./screens/policy/PayGateway'));
const AppleSigninCallback = lazy(() => import('./screens/AppleSigninCallback'));

const Router = ({ children }: { children: any }) => {
  const isElectron = useIsElectron();

  if (isElectron === true) {
    return <MemoryRouter initialEntries={['/upload']}>{children}</MemoryRouter>;
  }
  return <BrowserRouter>{children}</BrowserRouter>;
};

const PrivateRoute = () => {
  const { getRefreshToken } = useAuth();
  const { requestUserInfo } = useFetchUser();
  const [{ userInfo }] = useRecoilState(userState);
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return <Navigate replace to="/signin" />;
  }

  useEffect(() => {
    if (refreshToken && !userInfo) {
      requestUserInfo();
    }
  }, []);

  return <Outlet />;
};

const Layout = () => {
  useTutorial();
  useAnnouncement();

  const navigate = useNavigate();
  const location = useLocation();
  const isElectron = useIsElectron();
  const { requestUserInfo } = useFetchUser();
  const { requestAxios } = useAxios();
  const { setAccessToken, setRefreshToken, logOut } = useAuth();

  const requestAccessToken = async (temp_token: string, host: string) => {
    try {
      const data = await requestAxios<{
        access_token: string;
        refresh_token: string;
      }>(false, {
        url: `/users/${host}/get_${host}_access_token/`,
        method: 'post',
        data: { temp_token },
      });

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);

      navigate('/upload');
    } catch (error) {
      logOut();
      alertMessage('로그인에 실패했습니다. 다시 시도해 주세요.');
      navigate('/signin');
    }
  };

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, [location]);

  useEffect(() => {
    if (window?.electron) {
      window.electron.onReceivedToken(
        (
          _event: Electron.IpcRendererEvent,
          payload: { token: string; host: string }
        ) => {
          console.log('토큰 수신:', payload.token, '호스트:', payload.host);
          requestAccessToken(payload.token, payload.host);
        }
      );

      window.electron.onPaymentFinished(
        (_event: Electron.IpcRendererEvent, payload: { status: string }) => {
          console.log('status : ', payload.status);

          if (payload.status === 'success') {
            alertMessage('결제가 완료되었습니다.');
          } else if (payload.status === 'fail') {
            alertMessage('결제에 실패했습니다.');
          } else if (payload.status === 'cancel') {
            alertMessage('결제가 취소되었습니다.');
          }
          requestUserInfo();
        }
      );
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type !== 'PAYMENT') {
        return;
      }

      if (event.origin !== getOrigin()) {
        return;
      }
      if (event.data.type === 'PAYMENT') {
        window.focus();
        setTimeout(() => {
          alertMessage(event.data.payload);
          navigate('/my-page');
        }, 10);
      }
    };

    const handlePreloadError = () => window.location.reload();

    window.addEventListener('vite:preloadError', handlePreloadError);
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('vite:preloadError', handlePreloadError);
    };
  }, []);

  useEffect(() => {
    if (!isElectron) {
      return;
    }

    const url = window.location.href;
    const parsedUrl = new URL(url);
    const pathAndQuery = parsedUrl.pathname + parsedUrl.search;

    navigate(pathAndQuery);
  }, [window.location.href]);

  return (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100vw',
            height: '100vh',
            backgroundColor: 'transparent',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 100,
          }}
        >
          <Spinner />
        </div>
      }
    >
      <Banner />
      <Routes>
        {/* 로그인이 필요한 페이지 */}
        <Route element={<PrivateRoute />}>
          <Route path="/" element={<Upload />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/report" element={<Report />} />
          <Route path="/report/:id" element={<Detail />} />
          <Route path="/chatting" element={<Chatting />} />
          <Route path="/chatting/:id" element={<Chatting />} />
          <Route path="/my-page" element={<MyPage />} />
          <Route path="/exit" element={<Exit />} />
          <Route path="/charge" element={<Charge />} />
          <Route path="/announcement/admin" element={<AnnouncementAdmin />} />
          <Route
            path="/announcement/admin/:id"
            element={<AnnouncementAdmin />}
          />
          <Route
            path="/payment/electron/result"
            element={<PaymentElectronResult />}
          />
          <Route
            path="/payment/electron/fail"
            element={<PaymentElectronFail />}
          />
          <Route
            path="/payment/electron/cancel"
            element={<PaymentElectronCancel />}
          />
          <Route path="/payment/result" element={<PaymentResult />} />
          <Route path="/payment/fail" element={<PaymentFail />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Route>
        <Route path="/announcement" element={<Announcement />} />
        <Route path="/announcement/:id" element={<AnnouncementDetail />} />
        <Route path="/home" element={<Home />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/download" element={<Download />} />
        <Route path="/help" element={<Help />} />
        <Route path="/signin" element={<Signin />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/find" element={<FindId />} />
        <Route path="/find/list" element={<FindList />} />
        <Route path="/find/password" element={<FindPassword />} />
        <Route path="/find/reset" element={<ResetPassword />} />
        <Route path="/auth/kakao/callback" element={<KakaoCallback />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route
          path="/auth/kakao/callback/electron"
          element={<KakaoCallbackElectron />}
        />
        <Route
          path="/auth/google/callback/electron"
          element={<GoogleCallbackElectron />}
        />
        <Route path="/policy/privacy" element={<Privacy />} />
        <Route path="/policy/terms" element={<Terms />} />
        <Route path="/policy/period" element={<Period />} />
        <Route path="/policy/refund" element={<Refund />} />
        <Route path="/policy/personal-info" element={<PersonalInfo />} />
        <Route path="/policy/third-info" element={<ThirdInfo />} />
        <Route path="/policy/pay-gateway" element={<PayGateway />} />
        <Route
          path="/callbacks/sign_in_with_apple"
          element={<AppleSigninCallback />}
        />
        <Route path="/*" element={<Navigate to="/upload" />} />
        <Route path="*" element={<Navigate to="/upload" />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <RecoilRoot>
      <Router>
        <HelmetProvider>
          <Loading />
          <Modal />
          <MainAnnouncement />
          <Layout />
        </HelmetProvider>
      </Router>
    </RecoilRoot>
  );
}

export default App;
